import { setSentryUserContext } from '@/lib/sentry';
import { useOktaAuth } from '@okta/okta-react';
import { useEffect, useState } from 'react';
import { oktaClaimsToUserProfile, type UserProfile } from '../types';

/**
 * Hook to fetch and manage user profile information from Okta
 * Automatically sets user context in Sentry when profile is available
 */
export function useUserProfile() {
  const { oktaAuth, authState } = useOktaAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!authState?.isAuthenticated || !oktaAuth) {
        setUserProfile(null);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // Get user claims from the access token
        const userClaims = await oktaAuth.getUser();

        if (userClaims) {
          const profile = oktaClaimsToUserProfile(userClaims);
          setUserProfile(profile);

          // Set user context in Sentry for error reporting
          setSentryUserContext({
            id: profile.id,
            email: profile.email,
            name: profile.name || profile.preferred_username,
          });
        } else {
          setUserProfile(null);
        }
      } catch (err) {
        const error =
          err instanceof Error
            ? err
            : new Error('Failed to fetch user profile');
        setError(error);
        setUserProfile(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, [authState?.isAuthenticated, oktaAuth]);

  // Clear user profile and Sentry context when user logs out
  useEffect(() => {
    if (!authState?.isAuthenticated) {
      setUserProfile(null);
      setError(null);
    }
  }, [authState?.isAuthenticated]);

  return {
    userProfile,
    isLoading,
    error,
    /** Whether the user profile has been successfully loaded */
    isLoaded: !isLoading && !error && userProfile !== null,
    /** Refresh the user profile from Okta */
    refresh: async () => {
      if (authState?.isAuthenticated && oktaAuth) {
        try {
          setIsLoading(true);
          setError(null);
          const userClaims = await oktaAuth.getUser();
          if (userClaims) {
            const profile = oktaClaimsToUserProfile(userClaims);
            setUserProfile(profile);
            setSentryUserContext({
              id: profile.id,
              email: profile.email,
              name: profile.name || profile.preferred_username,
            });
          }
        } catch (err) {
          const error =
            err instanceof Error
              ? err
              : new Error('Failed to refresh user profile');
          setError(error);
        } finally {
          setIsLoading(false);
        }
      }
    },
  };
}
